import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import TranslateButton from '../components/TranslateButton';
import {
  MessageSquare,
  Heart,
  TrendingUp,
  Clock,
  Dog,
  HelpCircle,
  BookOpen,
  Image as ImageIcon,
  Newspaper,
  Loader2,
  Plus
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Forums = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentLanguage } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [translatedPosts, setTranslatedPosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Posts', icon: MessageSquare },
    { value: 'care-tips', label: 'Care Tips', icon: BookOpen },
    { value: 'identification', label: 'Breed ID', icon: HelpCircle },
    { value: 'showcase', label: 'Showcase', icon: ImageIcon },
    { value: 'question', label: 'Questions', icon: HelpCircle },
    { value: 'news', label: 'News', icon: Newspaper }
  ];

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = activeCategory === 'all' 
        ? `${API}/posts` 
        : `${API}/posts?category=${activeCategory}`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-muted text-muted-foreground',
      'care-tips': 'bg-primary/10 text-primary',
      'identification': 'bg-info/10 text-info',
      'showcase': 'bg-secondary/10 text-secondary',
      'question': 'bg-warning/10 text-warning',
      'news': 'bg-accent text-accent-foreground'
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Community Forums"
        description="Join discussions with fellow dog owners. Ask questions, share experiences, and connect with the AADC community."
        url="/forums"
      />
      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to post, comment, and interact with the community" />
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-heading font-medium">Discussion Forums</h1>
              <GuestAccessibleBadge />
            </div>
            <p className="text-muted-foreground text-lg">
              Share knowledge, ask questions, and connect with fellow dog lovers
            </p>
          </div>
          {user && (
            <Button className="btn-primary" onClick={() => navigate('/feed')}>
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Categories</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="flex flex-col">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setActiveCategory(cat.value)}
                      className={`flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        activeCategory === cat.value
                          ? 'bg-primary/10 text-primary border-r-2 border-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`category-${cat.value}`}
                    >
                      <cat.icon className="w-4 h-4" />
                      {cat.label}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Tabs defaultValue="latest" className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  <TabsTrigger value="latest" className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Latest
                  </TabsTrigger>
                  <TabsTrigger value="popular" className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Popular
                  </TabsTrigger>
                </TabsList>
                <Button className="btn-primary" onClick={() => navigate('/feed')} data-testid="new-discussion-btn">
                  Start Discussion
                </Button>
              </div>

              <TabsContent value="latest">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : posts.length === 0 ? (
                  <Card className="text-center py-16">
                    <CardContent>
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                        <Dog className="w-10 h-10 text-muted-foreground" />
                      </div>
                      <h3 className="font-heading text-xl mb-2">No discussions yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Be the first to start a conversation in this category!
                      </p>
                      <Button className="btn-primary" onClick={() => navigate('/feed')}>
                        Start Discussion
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {posts.map((post) => (
                      <Card 
                        key={post.id}
                        className="card-interactive cursor-pointer"
                        onClick={() => navigate(`/post/${post.id}`)}
                        data-testid={`forum-post-${post.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            <Avatar 
                              className="w-10 h-10 cursor-pointer shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/profile/${post.author_username}`);
                              }}
                            >
                              <AvatarImage src={post.author_avatar} />
                              <AvatarFallback>{post.author_username?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h3 className="font-heading font-medium text-lg truncate">
                                  {translatedPosts[post.id]?.title || post.title}
                                </h3>
                                <Badge className={`shrink-0 ${getCategoryColor(post.category)}`}>
                                  {post.category}
                                </Badge>
                              </div>
                              
                              <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                                {translatedPosts[post.id]?.content || post.content}
                              </p>
                              
                              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                                <span 
                                  className="hover:text-primary cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/profile/${post.author_username}`);
                                  }}
                                >
                                  @{post.author_username}
                                </span>
                                <span>•</span>
                                <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Heart className="w-3 h-3" />
                                  {post.likes_count}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3" />
                                  {post.comments_count}
                                </span>
                                {/* Translate Button - Show if not English */}
                                {currentLanguage !== 'en' && (
                                  <TranslateButton
                                    text={`${post.title}\n\n${post.content}`}
                                    onTranslated={(translated) => {
                                      const [title, ...contentParts] = translated.split('\n\n');
                                      setTranslatedPosts(prev => ({
                                        ...prev,
                                        [post.id]: {
                                          title: title || post.title,
                                          content: contentParts.join('\n\n') || post.content
                                        }
                                      }));
                                    }}
                                    contentType="forum"
                                    size="sm"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="popular">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...posts].sort((a, b) => b.likes_count - a.likes_count).map((post) => (
                      <Card 
                        key={post.id}
                        className="card-interactive cursor-pointer"
                        onClick={() => navigate(`/post/${post.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            <Avatar className="w-10 h-10 shrink-0">
                              <AvatarImage src={post.author_avatar} />
                              <AvatarFallback>{post.author_username?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h3 className="font-heading font-medium text-lg truncate">
                                  {post.title}
                                </h3>
                                <Badge className={`shrink-0 ${getCategoryColor(post.category)}`}>
                                  {post.category}
                                </Badge>
                              </div>
                              
                              <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                                {post.content}
                              </p>
                              
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>@{post.author_username}</span>
                                <span>•</span>
                                <span className="flex items-center gap-1 text-secondary">
                                  <Heart className="w-3 h-3 fill-current" />
                                  {post.likes_count}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3" />
                                  {post.comments_count}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forums;
